import sortBy from 'lodash/sortBy';
import tools from 'altrac-conversions';
import { get } from './translations/intl.js';
import statusWindMachine from './WindMachine.js';
import * as Pump from './Pump.js';
import Pump2 from './Pump2.js';
import { statusValve } from './Valve.js';
import { statusTemperature } from './Temperature.js';
import { statusWeather } from './Weather.js';
import { statusMoistureSensor } from './MoistureSensor.js';

function formatTemplate(template, vars = {}) {
  return String(template).replace(
    /\{(\w+)\}/g,
    (match, key) => (Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match),
  );
}

export default function statusCalculate(device = {}, _language, callback) {
  let language = 'en';
  if (typeof _language === 'string' && (_language === 'en' || _language === 'es')) {
    language = _language;
  }

  const reading0 = device.reading0 ?? {};
  const status = {
    code: 'normal',
    shortMessage: 'OK',
    message: 'No Issues',
    id: 'none',
    other: [],
    deviceHeadlineDanger: {
      enabled: false,
      text: [],
      color: 'danger',
    },
    deviceHeadlineWarning: {
      enabled: false,
      text: [],
      color: 'warning',
    },
    warningIndicator: false,
    stateIndicator: false,
  };

  let deviceType = device.physical?.deviceType ?? 'Unknown';
  if (
    device.interface_versioned?.data?.other &&
    typeof device.interface_versioned.data.other.deviceType === 'string'
  ) {
    deviceType = device.interface_versioned.data.other.deviceType;
  }

  let statusType;
  if (
    device.interface_versioned?.data?.other &&
    typeof device.interface_versioned.data.other.statusType === 'string'
  ) {
    statusType = device.interface_versioned.data.other.statusType;
  }

  let connected = true;
  const currentTime = Date.now();
  let sleepInterval = reading0['135'] * 1000;
  let resetTimeAllowed = 6 * 60 * 1000;
  if (reading0['135'] === 0 || reading0['135'] === undefined) {
    sleepInterval = 10 * 60 * 1000;
    resetTimeAllowed = 3 * 60 * 1000;
  }

  const nextSeen = new Date(reading0.date).getTime() + sleepInterval + 30 * 1000;
  const missedCheckinMilliseconds = currentTime - nextSeen;
  const minutesPast = Math.round(missedCheckinMilliseconds / 1000 / 60);

  if (missedCheckinMilliseconds > resetTimeAllowed * 11) {
    connected = false;
    status.other.push({
      id: 'notConnected',
      rank: 1,
      code: 'deviceProblem',
      category: 'device',
      shortMessage: 'Not Connected',
      message: `Device ${device.physical.deviceNumber} is ${minutesPast} minutes late reporting`,
    });
    status.deviceHeadlineDanger.enabled = true;
    status.deviceHeadlineDanger.text.push(
      formatTemplate(
        get(language, 'headline.notConnectedSince', 'Altrac device has not connected to the cloud since {sinceTime}'),
        { sinceTime: new Date(reading0.date).toLocaleDateString() },
      ),
    );
  } else if (missedCheckinMilliseconds > resetTimeAllowed * 7) {
    status.other.push({
      id: 'deviceMissedCheckinDanger',
      rank: 5,
      code: 'danger',
      category: 'device',
      shortMessage: 'Not Connected',
      message: `Device ${device.physical.deviceNumber} is ${minutesPast} minutes late reporting`,
    });
    status.deviceHeadlineDanger.enabled = true;
    status.deviceHeadlineDanger.text.push(
      formatTemplate(
        get(language, 'headline.notConnectedXMinutes', 'Altrac device has not checked in with cloud for {minutes} minutes.'),
        { minutes: minutesPast },
      ),
    );
  } else if (missedCheckinMilliseconds > resetTimeAllowed * 3) {
    status.other.push({
      id: 'deviceMissedCheckinWarning',
      rank: 100,
      code: 'normal',
      category: 'device',
      shortMessage: 'Not Connected',
      message: `Device ${device.physical.deviceNumber} is ${minutesPast} minutes late reporting`,
    });
    status.warningIndicator = true;
    status.deviceHeadlineWarning.enabled = true;
    status.deviceHeadlineWarning.text.push(
      formatTemplate(
        get(language, 'headline.notConnectedXMinutes', 'Altrac device has not checked in with cloud for {minutes} minutes.'),
        { minutes: minutesPast },
      ),
    );
  } else {
    status.other.push({
      id: 'connected',
      rank: 100,
      code: 'normal',
      category: 'device',
      shortMessage: 'Connected',
      message: `Device ${device.physical.deviceNumber} is connected`,
    });
  }

  if (connected) {
    if (deviceType === 'windMachine') statusWindMachine(status, device, language);
    if (deviceType === 'coldAirDrain') statusWindMachine(status, device, language);
    if (deviceType === 'pumpFrostWater') statusWindMachine(status, device, language);
    if (deviceType === 'pump') {
      if (statusType === 'pump2') {
        Pump2(status, device, language);
      } else {
        Pump.status(status, device, language);
      }
    }
    if (deviceType === 'flow') Pump.status(status, device, language);
    if (deviceType === 'valve') statusValve(status, device, language);
    if (deviceType === 'temperature') statusTemperature(status, device, language);
    if (deviceType === 'weather') statusWeather(status, device);
    if (deviceType === 'moistureSensor') statusMoistureSensor(status, device);
  } else if (deviceType === 'windMachine') {
    statusWindMachine.offline(status, device, language);
  }

  const batteryInternalWarning = 0.2;
  const batteryInternal = tools.isNumber(reading0.IB) ? reading0.IB : reading0['129'];

  // Skip warning when battery is 0 (damaged port / non-reading); warn only for low but non-zero levels (e.g. 0.1, 0.15)
  if (batteryInternal > 0 && batteryInternal < batteryInternalWarning) {
    status.other.push({
      id: 'batteryLow',
      rank: 100,
      code: 'normal',
      category: 'sensor',
      shortMessage: 'Altrac Battery Low',
      message: `Int Batt at ${device.physical.deviceNumber} is ${Math.round(batteryInternal * 100)}% which is below the ${Math.round(batteryInternalWarning * 100)}% warning level.`,
    });
    if (connected) {
      status.warningIndicator = true;
      status.deviceHeadlineWarning.enabled = true;
      status.deviceHeadlineWarning.text.push(
        get(language, 'heading.internalBatteryLow', 'Altrac internal battery low'),
      );
    }
  }

  status.other = sortBy(status.other, (o) => o.rank);

  if (status.other.length > 0) {
    const first = status.other[0];
    status.code = first.code;
    status.id = first.id;
    status.category = 'multiple';
    status.shortMessage = first.shortMessage;
    status.message = first.message;
  }
  // when other is empty, keep initial defaults (code: 'normal', shortMessage: 'OK', etc.)

  if (callback) callback(null, status);
  return status;
}
